import React, { useState, useLayoutEffect, useEffect, useContext } from 'react';
import { View, StyleSheet } from 'react-native';
import HeaderButton from '../../../components/Buttons/HeaderButton';
import UserForm from './components/UserForm';
import { HeaderBackButton } from '@react-navigation/stack';
import CustomSnackbar from '../../../components/CustomSnackbar';
import LinkStyleButton from '../../../components/Buttons/LinkStyleButton';
import { MainColors } from '../../../constants/Colors';
import FontSizes from '../../../constants/FontSizes';
import DeleteModal from '../../../components/Modals/DeleteModal';
import LoadingIndicator from '../../../components/ActivityIndicator';
import { OrganizationContext } from '../../../context/OrganizationProvider';
import useFetch from '../../../utils/useFetch';

const EditUserScreen = ({ route, navigation }) => {
    const { userId } = route.params;
    const { organizationId } = useContext(OrganizationContext);
    const { get, put, doDelete, isLoading } = useFetch();
    const url = `/api/users/${userId}?organizationId=${organizationId}`;

    const [initialUserData, setInitialUserData] = useState();
    const [email, setEmail] = useState();
    const [isAdmin, setIsAdmin] = useState();
    const [isMember, setIsMember] = useState();

    const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
    const [snackbarVisible, setSnackbarVisible] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isModalDeleteButtonDisabled, setIsModalDeleteButtonDisabled] = useState(true);
    const [deleteInputValue, setDeleteInputValue] = useState();

    useEffect(() => {
        async function getUserData() {
            const response = await get(url);
            setEmail(response.email);
            setIsAdmin(response.isAdminForOrganization);
            setIsMember(response.isVoterInOrganization);

            setInitialUserData({
                email: response.email,
                isAdmin: response.isAdminForOrganization,
                isMember: response.isVoterInOrganization
            })
        }
        getUserData();
    }, []);

    useEffect(() => {
        function checkForFormChanges() {
            if (email && email !== initialUserData.email
                || isAdmin !== initialUserData.isAdmin
                || isMember !== initialUserData.isMember) {
                setSaveButtonDisabled(false);
            } else {
                setSaveButtonDisabled(true);
            }
        }

        if (initialUserData) {
            checkForFormChanges();
        }

    }, [email, isAdmin, isMember]);

    const onUpdateUserPressedHandler = async () => {
        await put(url, {
            userId: userId,
            email: email,
            isAdminForOrganization: isAdmin,
            isVoterInOrganization: isMember
        });

        setSaveButtonDisabled(true);
        setSnackbarMessage('User changes were saved.');
        setSnackbarVisible(true);
    }

    const onDeleteInputTextChangedHandler = (deleteInputValue) => {
        setDeleteInputValue(deleteInputValue);
        if (deleteInputValue === 'DELETE') {
            setIsModalDeleteButtonDisabled(false);
        } else {
            setIsModalDeleteButtonDisabled(true);
        }
    }

    const onDeleteUserPressedHandler = async () => {
        await doDelete(url, { userId: userId });
        setIsDeleteModalVisible(false);
        setSaveButtonDisabled(true);
        setSnackbarMessage('User was successfully deleted.');
        setSnackbarVisible(true);
    }

    const onSnackbarDismissedHandler = () => {
        setSnackbarVisible(false);
        navigation.navigate('AdminUsersDashboard');
    }

    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: 'Edit User',
            headerRight: () => (
                <HeaderButton
                    onPress={onUpdateUserPressedHandler}
                    accessibilityLabel="Save changes for user"
                    accessibilityHint="Press this button to save changes for this user"
                    buttonText="Save"
                    disabled={saveButtonDisabled}
                />
            ),
            headerLeft: () => (
                <HeaderBackButton
                    onPress={() => { navigation.goBack() }}
                    label="Back"
                    tintColor="#fff"
                />
            )
        });
    }, [navigation, saveButtonDisabled, snackbarVisible]);

    return (
        <View style={styles.screen}>
            <LoadingIndicator isLoading={isLoading}>
                {initialUserData ? (
                    <>
                        <View style={styles.contentContainer}>
                            <UserForm
                                isEmailEditable={false}
                                emailValue={email}
                                isAdminChecked={isAdmin}
                                onAdminPressed={() => setIsAdmin(!isAdmin)}
                                isMemberChecked={isMember}
                                onMemberPressed={() => setIsMember(!isMember)}
                                formButtonText='Save Changes'
                                formButtonAccessibilityLabel='Save Changes'
                                formButtonAccessibilityHint='Press this button to save changes for this user'
                                formButtonDisabled={saveButtonDisabled}
                                formButtonPressed={onUpdateUserPressedHandler}
                            />
                            <LinkStyleButton
                                style={styles.deleteButton}
                                buttonText='Delete User'
                                accessibilityLabel='Delete User'
                                accessibilityHint='Press this button to delete this user'
                                onPress={() => setIsDeleteModalVisible(true)}
                            />
                        </View>
                        <CustomSnackbar
                            message={snackbarMessage}
                            visible={snackbarVisible}
                            onDismiss={onSnackbarDismissedHandler}
                            actionLabel='GO BACK'
                            actionOnPress={() => navigation.navigate('AdminUsersDashboard')}
                        />
                        <DeleteModal
                            visible={isDeleteModalVisible}
                            deleteButtonDisabled={isModalDeleteButtonDisabled}
                            onModalBackgroundPressed={() => setIsDeleteModalVisible(false)}
                            onCancelPressed={() => setIsDeleteModalVisible(false)}
                            itemToDelete={initialUserData.email}
                            deleteInputValue={deleteInputValue}
                            onChangeDeleteInputText={onDeleteInputTextChangedHandler}
                            onModalDeletePress={onDeleteUserPressedHandler}
                        />
                    </>
                ) : (null)}
            </LoadingIndicator>
        </View>
    )
}

const styles = StyleSheet.create({
    screen: {
        flexGrow: 1
    },
    contentContainer: {
        paddingVertical: 30,
        paddingHorizontal: 20
    },
    deleteButton: {
        marginTop: 20,
        color: MainColors.danger,
        fontWeight: '500',
        fontSize: FontSizes.medium
    }
});

export default EditUserScreen;